import { Component, computed, inject, input, OnInit, signal } from '@angular/core';

import { from, switchMap, catchError, EMPTY } from 'rxjs';
import { ServiceUpgradeInjectorService } from '@shims';
import { ButtonComponent } from '@ui-components';
import { MessagesEndpoints } from '@pims-endpoints';

interface WidgetResponseData {
  order_id?: string | number;
  status: string;
  message_id: number;
  [key: string]: unknown;
}

interface StateChangeSuccessEvent {
  name: string;
  defaultPrevented: boolean;
  preventDefault: () => void;
}

interface RouteConfig {
  url: string;
  pageTitle: string;
  templateUrl: string;
  controller: string;
  controllerAs: string;
  authenticate: boolean;
  name: string;
}

interface RouteParams {
  id: string;
  tab: string;
}

@Component({
  selector: 'lib-widget-refill-response',
  imports: [ButtonComponent],
  templateUrl: './widget-refill-response.component.html',
  styleUrls: ['../../../../../ui-components/src/lib/_styles/index.scss', './widget-refill-response.component.scss'],
})
export class WidgetRefillResponseComponent implements OnInit {
  private serviceUpgradeInjectorService = inject(ServiceUpgradeInjectorService);
  private messageEndpointService = new MessagesEndpoints();
  readonly data = input<WidgetResponseData>();
  readonly visitId = computed(() => this.data()?.order_id);
  readonly visitStatus = computed(() => (this.data()?.status || 'normal').toLowerCase());
  $rootScope = this.serviceUpgradeInjectorService.getAngularJsRootScope();

  isWithinVisit = signal(false);

  ngOnInit() {
    const currentState = this.$rootScope.navbar?.$state?.current?.name;
    this.isWithinVisit.set(currentState === 'visitDetail');
  }

  private navigate() {
    return from(
      new Promise<void>((resolve, reject) => {
        const unsubscribe = this.$rootScope.$on(
          '$stateChangeSuccess',
          (event: StateChangeSuccessEvent, toState: RouteConfig, toParams: RouteParams) => {
            if (toState.name === 'visitDetail' && toParams.id == this.visitId()) {
              unsubscribe();
              resolve();
            }
          },
        );

        const errorUnsubscribe = this.$rootScope.$on('$stateChangeError', () => {
          unsubscribe();
          errorUnsubscribe();
          reject(new Error('Navigation failed'));
        });

        this.$rootScope.$broadcast('dialog:navigate', {
          state: 'visitDetail',
          params: { id: this.visitId(), tab: 'refills' },
        });
      }),
    ).pipe(
      catchError(error => {
        console.error('Navigation failed:', error);
        throw error;
      }),
    );
  }

  private markMessageAsRead() {
    const message_id = this.data()?.message_id as number;
    return this.messageEndpointService.put(message_id, { read_at: 'now' }).pipe(
      catchError(error => {
        console.error('Mark message as read failed:', error);
        throw error;
      }),
    );
  }

  onNavigate(event: Event) {
    event.stopPropagation();
    if (this.isWithinVisit()) {
      return;
    }

    this.navigate()
      .pipe(
        switchMap(() => this.markMessageAsRead()),
        catchError(() => EMPTY),
      )
      .subscribe({
        next: response => console.log('POST request successful', response),
      });
  }
}
